import React from 'react';
import { Button, SparkIcon, sparkToast } from '@spark';
import { useExport } from '@/shared/hooks/useExport';
import { SubTaskType, TaskType } from '@/shared/domain/taskTypes';
/**
 * Reusable export button component for model responses.
 * Handles exporting model responses with filters and comparison options.
 */
export const ModelResponsesExportButton = ({
    currentUsecase,
    activeTab,
    version,
    llmUsecaseId,
    filters = {},
    isVersionComparison = false,
    comparisonVersion = null,
    comparisonModelName = "Teacher Model",
    disabled = false,
    role,
    isFileComparison = false,
    selectedDistillationEventId = null,
}) => {
    const { exportData } = useExport();

    const handleExport = () => {
        if (!currentUsecase?._id || !llmUsecaseId || !version) {
            sparkToast.error('Cannot export: missing required information');
            return;
        }

        const apiFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== null && value !== '' && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

        const requestData = {
            client_id: currentUsecase?.client_id,
            llm_usecase_id: llmUsecaseId,
            version: version,
            filters: Object.keys(apiFilters).length > 0 ? apiFilters : {},
            comparison_model_name: comparisonModelName,
            is_file_comparison: isFileComparison,
            sub_task_type: (currentUsecase?.task_type === TaskType.NER_WITH_RELATIONS) ?
                (activeTab === 'entities' ? SubTaskType.Ner : SubTaskType.Relations) : null,
            selected_distillation_event_id: selectedDistillationEventId || null,
        };

        if (isVersionComparison) {
            requestData.comparison_version = comparisonVersion;
            requestData.is_version_comparison = true;
        }

        let filename = `model_responses_v${version}_${llmUsecaseId}`;
        if (isVersionComparison && comparisonVersion) {
            filename += `_vs_v${comparisonVersion}`;
        } else {
            const modelName = comparisonModelName || "Teacher Model";
            filename += `_vs_${modelName.replace(/\s+/g, '_')}`;
        }
        filename += '.json';

        exportData(
            "download-model-responses",
            requestData,
            filename,
            () => {
                sparkToast.success('Model responses have been exported successfully.');
            },
            (error) => {
                sparkToast.error(error || 'Failed to export model responses');
            }
        );
    };

    return (
        <Button
            className="fs-unmask"
            variant="secondary"
            size="small"
            showStartIcon
            startIcon={<SparkIcon slug="download" size="small" />}
            onClick={handleExport}
            disabled={disabled || role === 'Viewer'}
        >
            Export
        </Button>
    );
};
